import { useEffect, useState } from "react";
import { GetResourceSearchResultsItem } from "@/api/getResourceSearchResults/types";
import { searchService } from "@/modules/hub/features/Search/services/searchService";

interface UseFeaturedResourcesOptions {
  context?: "library" | "library-v2" | "podcasts" | "partner-network"; // Which page is calling this
}

export function useFeaturedResources(
  options: UseFeaturedResourcesOptions = {},
) {
  const [featuredResources, setFeaturedResources] = useState<
    GetResourceSearchResultsItem[]
  >([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchFeaturedResources = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Initialize with appropriate context (will use secured key with resource type filters)
        await searchService.initialize(false, options.context);

        // Search for featured resources using Algolia
        const result = await searchService.searchResources(
          "", // Empty query to get all
          { type: "resources" }, // Only search resources
          0, // First page
          20, // Fetch up to 20 featured resources
          "newest", // Sort by newest
          false, // Don't use favorites key
        );

        // Filter to only featured resources (backend filter would be better, but this works)
        const featured = result.hits
          .filter((hit: any) => hit.isFeatured === true)
          .map((hit: any) => ({
            id:
              hit.resourceId ||
              hit.objectID?.replace("resource_", "") ||
              Math.random() * 100000,
            uuid: hit.uuid || "",
            slug: hit.slug || "",
            title: hit.title || "",
            searchDescription: hit.searchDescription || null,
            isPublished: hit.isPublished || false,
            thumbnailUrl: hit.thumbnail || null,
            thumbnailFileUuid: null,
            primaryIcon: hit.primaryIcon || null,
            isFeatured: hit.isFeatured || false,
            resourceType: hit.resourceType || null,
            // Algolia stores timestamps in seconds, convert to ISO string
            createdOrPublishStartDate:
              hit.publishStartDate || hit.createdAt
                ? new Date(
                    (hit.publishStartDate || hit.createdAt) * 1000,
                  ).toISOString()
                : null,
            viewCount: hit.viewCount || 0,
            backgroundColor: hit.backgroundColor || null,
            textColor: hit.textColor || null,
            borderColor: hit.borderColor || null,
            contentUrl: hit.url || null,
            roles: Array.isArray(hit.businessRoles)
              ? hit.businessRoles.map((name: string, idx: number) => ({
                  id: idx,
                  name,
                }))
              : [],
            trades: Array.isArray(hit.trades)
              ? hit.trades.map((name: string, idx: number) => ({
                  id: idx,
                  name,
                }))
              : [],
            processWheel: hit.processWheel || null,
            isFavorited: false,
          }));

        setFeaturedResources(featured);
      } catch (err) {
        console.error("Error fetching featured resources:", err);
        setError("Failed to load featured resources");
        setFeaturedResources([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchFeaturedResources();
  }, [options.context]);

  return {
    featuredResources,
    isLoading,
    error,
  };
}
